import { isBrowser } from "../environment";

export type FlushReason = "timer" | "hidden";

export interface FlushSchedulerOptions {
	intervalMs: number;
	onFlush: (reason: FlushReason) => void;
}

/**
 * The time- and visibility-based flush triggers. The size trigger belongs to whoever owns
 * the queue, since only it knows how full the queue is.
 *
 * `visibilitychange` to hidden is the reliable end-of-page signal — `beforeunload` and
 * `unload` do not fire on mobile when an app is backgrounded or the tab is discarded.
 */
export class FlushScheduler {
	private readonly options: FlushSchedulerOptions;
	private timer: ReturnType<typeof setInterval> | null = null;
	private readonly onVisibilityChange = () => {
		if (document.visibilityState === "hidden") {
			this.options.onFlush("hidden");
		}
	};

	constructor(options: FlushSchedulerOptions) {
		this.options = options;
	}

	start(): void {
		if (!isBrowser() || this.timer !== null) return;

		this.timer = setInterval(() => this.options.onFlush("timer"), this.options.intervalMs);
		document.addEventListener("visibilitychange", this.onVisibilityChange);
	}

	stop(): void {
		if (this.timer !== null) {
			clearInterval(this.timer);
			this.timer = null;
		}
		if (isBrowser()) {
			document.removeEventListener("visibilitychange", this.onVisibilityChange);
		}
	}
}
